import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { AppRoutes } from '@/constants/appRoutes'
import {
  finalSession,
  SecurityError,
  securityErrorKey,
  sessionSchema,
  useSensitiveRequest,
} from '@/features/security/securityClient'
import type {
  FactorMethod,
  FinalSession,
  MfaChallenge,
} from '@/features/security/securityClient'
import { FactorFields } from '@/features/security/SecurityFields'

export function MfaChallengeForm({
  challenge,
  onComplete,
  onCancel,
}: {
  challenge: MfaChallenge
  onComplete: (session: FinalSession) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const request = useSensitiveRequest()
  const [method, setMethod] = useState<FactorMethod>('totp')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [expired, setExpired] = useState(false)
  const lock = useRef(false)
  const heading = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    heading.current?.focus()
    const timer = setTimeout(() => {
      setExpired(true)
      setCode('')
    }, challenge.expiresIn * 1000)
    return () => clearTimeout(timer)
  }, [challenge])
  return (
    <div className="flex w-full max-w-[436px] flex-col gap-6">
      <h1 ref={heading} tabIndex={-1} className="text-2xl font-medium">
        {t('security.loginTitle')}
      </h1>
      <p>{t('security.loginHelp')}</p>
      {error && (
        <p role="alert" className="text-dynamicRed">
          {t(error)}
        </p>
      )}
      {expired && (
        <p role="alert">{t('security.errors.MFA_CHALLENGE_EXPIRED')}</p>
      )}
      <form
        className="flex flex-col gap-5"
        onSubmit={async (event) => {
          event.preventDefault()
          if (lock.current || expired) return
          lock.current = true
          setBusy(true)
          setError('')
          try {
            const result = await request(
              '/api/v1/auth/mfa/verify',
              sessionSchema,
              {
                body: {
                  challengeToken: challenge.challengeToken,
                  method,
                  code: code.trim(),
                },
              },
            )
            setCode('')
            if (method === 'recovery_code' && result.userStatus === 'active') {
              toast.info(t('security.recoveredLogin'), {
                action: {
                  label: t('security.title'),
                  onClick: () => navigate(AppRoutes.settingsSecurity),
                },
              })
            }
            onComplete(finalSession(result))
          } catch (e) {
            setCode('')
            setError(securityErrorKey(e))
            if (
              e instanceof SecurityError &&
              (e.status === 401 || e.code === 'MFA_CHALLENGE_EXPIRED')
            )
              setExpired(true)
          } finally {
            lock.current = false
            setBusy(false)
          }
        }}
      >
        <FactorFields
          method={method}
          code={code}
          onCode={setCode}
          disabled={busy || expired}
          onMethod={(next) => {
            setMethod(next)
            setCode('')
            setError('')
          }}
        />
        <Button type="submit" disabled={busy || expired} isLoading={busy}>
          {t('security.verify')}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={busy}
          onClick={() => {
            setCode('')
            onCancel()
          }}
        >
          {t('security.backToSignIn')}
        </Button>
      </form>
    </div>
  )
}
